import useCountdown from "@/hooks/common/useCountdown";
import useOverlay from "@/hooks/common/useOverlay";
import {useTaggingClick} from "@/hooks/common/useTagging";
import {getTarget} from "@COMPONENTS/common/commonUtil";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import {
	departureArrival,
	departureStart,
	flightLocation,
	getPrice,
	getTrafficIconUrl,
	isGoodsTpFH,
	returnArrival,
	returnStart,
	scheduleTimeText,
} from "@COMPONENTS/common/eventUtil";
import {getChdAgeRange, getInfAgeRange} from "@COMPONENTS/common/goodsUtil";
import Alert from "@COMPONENTS/common/modal/alert/Desktop";
import {PageProps} from "@COMPONENTS/product/pages/detailPackage/common/types";
import KeyInformationPop from "@COMPONENTS/product/pages/detailPackage/desktop/popup/KeyInformationPop";
import {DayTimes, Days} from "@COMPONENTS/product/pages/detailPackage/desktop/schedule/DailySchedule";
import * as style from "@COMPONENTS/style/detailpackage/itinerary";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {PlusMoreButton, TextButton} from "@FRAMEWORK/ui/desktop/button";
import AirlineIcon from "@FRAMEWORK/ui/desktop/icon/AirlineIcon";
import {Flex} from "@FRAMEWORK/ui/desktop/layout";
import {BulletListItem, StackListItem} from "@FRAMEWORK/ui/desktop/list";
import {TableContainer} from "@FRAMEWORK/ui/desktop/table";
import {BlueText, SubTitle} from "@FRAMEWORK/ui/desktop/text";
import errorImg from "@PUBLIC/P/images/ico/ico_nodata.png";
import useSigninUser from "@QUERIES/account/useSigninUser";
import useGlobalGoodsLive, {GlobalGoodsLive} from "@QUERIES/bff/global/useGlobalGoodsLive";
import useGlobalMenu from "@QUERIES/bff/global/useGlobalMenu";
import useBannerContents from "@QUERIES/essential/banner/useBannerContents";
import useBannerGoodsDeal, {BannerGoodsDeal} from "@QUERIES/essential/banner/useBannerGoodsDeal";
import useCouponByEvent, {CouponByEvent} from "@QUERIES/prdt/coupon/useCouponByEvent";
import couponDownAsync, {RequestBody} from "@QUERIES/prdt/coupon/useCouponDown";
import useEventFirstDisplay from "@QUERIES/prdt/event/useEventFirstDisplay";
import useEventPrice from "@QUERIES/prdt/event/useEventPrice";
import useEventScheduleTrip from "@QUERIES/prdt/event/useEventScheduleTrip";
import useEventTourDetail from "@QUERIES/prdt/event/useEventTourDetail";
import {useQueryClient} from "@tanstack/react-query";
import {format} from "@yb/utils";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import React, {SyntheticEvent, useMemo, useState} from "react";

const daykor = ["일", "월", "화", "수", "목", "금", "토"];

const BannerYellowPangDeal = ({deal}: {deal: BannerGoodsDeal}) => {
	const PangdealText = () => {
		const targetDate = dayjs(deal.dealEdTm).add(1, "minute");
		const countdown = useCountdown(targetDate);
		return (
			<style.PangdealText>
				<p>
					<style.PangdealProduct>{deal.dealType === "T" ? "타임특가" : "선착순특가"}</style.PangdealProduct>
					&nbsp;
					<style.PangdealDate>
						{deal.dealType === "T" ? countdown ?? "종료" : `상품 ${deal.dealRervCnt || 0}개`}
					</style.PangdealDate>
					{countdown !== null && ` 남음`}
				</p>
				<p>{`${
					deal.dealRervCnt > 0 || countdown !== null
						? `${format.koKRFormatNumber(deal.price - deal.dealGoodsPrice)}원 즉시할인가격`
						: ``
				}`}</p>
			</style.PangdealText>
		);
	};

	return (
		<style.BannerYellowpangdeal>
			<a target="_self" href={`${publicRuntimeConfig.ENV_PACKAGE_URL}/promotion/yellowPangDeal.yb`}>
				<style.PangdealPrice>
					<PangdealText />
				</style.PangdealPrice>
				<style.PangdealLink>
					<span>전체보기</span>
				</style.PangdealLink>
			</a>
		</style.BannerYellowpangdeal>
	);
};

const BannerYellowLive = ({live}: {live: GlobalGoodsLive}) => {
	dayjs.extend(isBetween); // isBetween 사용
	const {dspYn, liveDt, liveTime} = live;
	// 방송 노출 조건 Y가 아닌 경우 - 노출 X
	if (dspYn !== "Y") return <></>;

	const liveStartDate = dayjs(liveDt);
	const liveEndDate = liveStartDate.add(Number(liveTime), "minute");

	const today = dayjs();

	const LiveText = () => {
		if (today.isBefore(liveStartDate)) {
			const countdown = useCountdown(liveStartDate);
			return (
				<>
					<style.LiveDate>COMING SOON</style.LiveDate>
					<style.LiveState>{countdown}</style.LiveState>
				</>
			);
		} else if (today.isBetween(liveStartDate, liveEndDate)) {
			return (
				<>
					<style.LiveDate>ON AIR</style.LiveDate>
					<style.LiveState>실시간 방송 중</style.LiveState>
				</>
			);
		} else {
			const formattedDate = `${liveStartDate.format("MM/DD")}(${daykor[liveStartDate.day()]}) ${liveStartDate.format(
				"HH:mm",
			)}`;
			return (
				<>
					<style.LiveDate>{formattedDate}</style.LiveDate>
					<style.LiveState>방영 완료</style.LiveState>
				</>
			);
		}
	};

	return (
		<style.BannerYellowLive>
			<a href={`${publicRuntimeConfig.ENV_PORTAL_URL}/promotion/yellowLive`}>
				<style.LiveText>
					<style.LiveTit>최대 혜택으로 떠나는 생생한 여행</style.LiveTit>
					{/* LiveText */}
					<LiveText />
				</style.LiveText>
				<style.LiveLink>
					<span>바로가기</span>
				</style.LiveLink>
			</a>
		</style.BannerYellowLive>
	);
};

const DetailBannerComponent = (props: {goodsCd: string; goodsNm: string}) => {
	const bannerContentsFetch = useBannerContents("GOPM"); // WEB_CONT_DIVI_CD 상품상세_요금영역
	const bannerContents = bannerContentsFetch.data?.body;

	const bannerDealFetch = useBannerGoodsDeal(props.goodsCd);
	const bannerDeal = bannerDealFetch.data?.body;

	const bannerLiveFetch = useGlobalGoodsLive(props.goodsCd);
	const bannerLive = bannerLiveFetch.data?.body;

	const imageOnError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
		e.currentTarget.src = errorImg;
		e.currentTarget.className = "img-error";
	};

	const {taggingClick} = useTaggingClick("pkg_select_promo_dtl", {selectDiv: "MiddtlBanner"});

	return (
		<>
			<style.EventBanner>
				{bannerContents &&
					bannerContents.map((item, index) => (
						<a
							href={`${publicRuntimeConfig.ENV_PACKAGE_URL}${item.link}`}
							key={index}
							target={getTarget(item.target)}
							onClick={() => taggingClick(item)}
						>
							<img
								src={`${publicRuntimeConfig.ENV_CIMG_CDN_URL}${item.imgPath}`}
								alt={item.alt}
								onError={imageOnError}
							/>
						</a>
					))}
			</style.EventBanner>
			{bannerDeal && <BannerYellowPangDeal deal={bannerDeal} />}
			{bannerLive && <BannerYellowLive live={bannerLive} />}
		</>
	);
};

const DetailBanner = (props: {goodsCd: string; goodsNm: string}) => (
	<ErrorBoundary logMsg="DetailContents DetailBanner Error">
		<DetailBannerComponent {...props} />
	</ErrorBoundary>
);

const DetailNoticeComponent = (props: Pick<PageProps, "eventDetail" | "query" | "usePrintView">) => {
	const {eventDetail, query, usePrintView = false} = props;
	const goodsAreaOne = eventDetail.evCd.substring(0, 1);
	const {evCd, goodsCd, simpleScheYn, outUseTmhh, outUseTmmi, inUseTmhh, inUseTmmi} = eventDetail;

	const scheduleTripFetch = useEventScheduleTrip(evCd, goodsCd);
	const tourDetailsFetch = useEventTourDetail(evCd);

	/** 간략 일정 더보기 */
	const [showMore, setShowMore] = useState(usePrintView);
	const buttonText = showMore ? "접기" : "더 보기";
	const handleScheduleButton = () => {
		setShowMore(!showMore);
	};

	const simpleDays = useMemo(() => {
		if (!scheduleTripFetch.isFetched || !tourDetailsFetch.isFetched) return null;
		const {
			scheduleDetail,
			scheduleDetailTm,
		}: {
			scheduleDetail: Array<Days>;
			scheduleDetailTm: Array<DayTimes>;
		} = scheduleTripFetch.data.body;
		const tourDetails = tourDetailsFetch.data?.body;

		const result = scheduleDetail.map(detail => {
			const dayTmList = scheduleDetailTm.filter(tm => {
				tm.tourDetails = tourDetails.filter(v => v.tmSeq === tm.tmSeq);
				return tm.dayNo === detail.dayNo;
			});

			const cities = dayTmList.reduce((accCity, city) => {
				// 이미 Array에 등록된 바로 이전 city
				const recentIndex = accCity.length - 1 || 0;
				const prevCity = accCity.length > 0 && accCity[recentIndex].code === city.cityCd ? accCity[recentIndex] : null;
				if (prevCity) {
					const combined = {
						code: city.cityCd,
						city: city.cityNm,
						tourlist: [
							...prevCity.tourlist,
							...city.tourDetails.map(item => `${item.trvCd === "70" ? "[선택관광]" : ""}${item.trvInfoNm ?? ""}`),
						],
					};
					accCity.splice(recentIndex, 1, combined);
				} else {
					const tourlist = city.tourDetails.map(
						item => `${item.trvCd === "70" ? "[선택관광]" : ""}${item.trvInfoNm ?? ""}`,
					);
					accCity.push({code: city.cityCd, city: city.cityNm, tourlist});
				}
				return accCity;
			}, []);
			return {dayNo: detail.dayNo, dayNote: detail.dayNote, cities};
		});

		return result;
	}, [scheduleTripFetch, tourDetailsFetch]);

	const location = useMemo(() => {
		if (!scheduleTripFetch.isFetched) return null;
		const scheduleDetailTm = scheduleTripFetch.data?.body?.scheduleDetailTm;
		return flightLocation(scheduleDetailTm, goodsAreaOne);
	}, [scheduleTripFetch]);

	// 여행상품 핵심정보 팝업
	const usePrice = useEventPrice(evCd);
	const price = usePrice.data?.body;
	const [open] = useOverlay();
	const openKeyInformation = () => {
		open(overlay => <KeyInformationPop {...overlay} event={eventDetail} price={price} />);
	};

	/** 예약현황 */
	const firstDisplay = useEventFirstDisplay(evCd).data.body;
	const globalMenu = useGlobalMenu().data?.body;
	const {menuLv2List} = globalMenu;
	const isReserveCount = useMemo(() => {
		if (!menuLv2List) return;
		const dspSid = query?.dspSid ? `${query.dspSid?.slice(0, -4)}0000` : firstDisplay?.dspSid2 || "";
		const targetMenu = menuLv2List.find(v => v.dspSid === dspSid);
		return targetMenu ? targetMenu.tempRervCntYn : null;
	}, [menuLv2List, firstDisplay]);

	return (
		<>
			<style.SubTitleFlexWrap>
				<SubTitle>여행 주요일정</SubTitle>
				{!usePrintView && <TextButton text="여행상품 핵심정보" size="default" onClick={openKeyInformation} />}
			</style.SubTitleFlexWrap>

			<style.SimpleItinerary>
				<style.ItineraryTable>
					<table>
						<caption>여행 상품 주요일정 요약</caption>
						<colgroup>
							<col style={{width: "16.91%"}} />
							<col style={{width: "83.09%"}} />
						</colgroup>
						<tbody>
							<tr>
								<th scope="row">일정</th>
								<td>
									<style.ItineraryAirplaneInfo>
										<style.AirplaneInfoContent>
											<Flex align="flex-start">
												<style.AirplaneDepart>출발</style.AirplaneDepart>
												<Flex direction="column" gap="10">
													<Flex align="center">
														{eventDetail.trTp ? (
															<AirlineIcon
																img={getTrafficIconUrl(eventDetail)}
																title={`${eventDetail.trCompanyNm} ${
																	eventDetail.seatClassCd && eventDetail.seatClassCd !== "EC"
																		? `(${eventDetail.seatClassNm})`
																		: ""
																}`}
															/>
														) : (
															<>교통불포함</>
														)}
														<style.AirplaneNumber>{eventDetail.outFlightNm}</style.AirplaneNumber>
														{scheduleTimeText({hh: outUseTmhh, mi: outUseTmmi}) && (
															<style.AirplaneTime>
																{scheduleTimeText({hh: outUseTmhh, mi: outUseTmmi})}
															</style.AirplaneTime>
														)}
													</Flex>
													<Flex align="center">
														{location?.outDeprtCityNm && (
															<style.AirplaneName>{location.outDeprtCityNm}</style.AirplaneName>
														)}
														<style.AirplaneDay>{departureStart(eventDetail)}</style.AirplaneDay>
														<style.AirplaneIcon />
														{location?.outArrvCityNm && (
															<style.AirplaneName>{location.outArrvCityNm}</style.AirplaneName>
														)}
														<style.AirplaneDay>{departureArrival(eventDetail)}</style.AirplaneDay>
													</Flex>
												</Flex>
											</Flex>
											<Flex align="flex-start">
												<style.AirplaneDepart>도착</style.AirplaneDepart>
												<Flex direction="column" gap="10">
													<Flex align="center">
														{eventDetail.trTp ? (
															<AirlineIcon
																img={getTrafficIconUrl(eventDetail)}
																title={`${eventDetail.trCompanyNm} ${
																	eventDetail.seatClassCd && eventDetail.seatClassCd !== "EC"
																		? `(${eventDetail.seatClassNm})`
																		: ""
																}`}
															/>
														) : (
															<>교통불포함</>
														)}
														<style.AirplaneNumber>{eventDetail.inFlightNm}</style.AirplaneNumber>
														{scheduleTimeText({hh: inUseTmhh, mi: inUseTmmi}) && (
															<style.AirplaneTime>
																{scheduleTimeText({hh: inUseTmhh, mi: inUseTmmi})}
															</style.AirplaneTime>
														)}
													</Flex>
													<Flex align="center">
														{location?.inDeprtCityNm && (
															<style.AirplaneName>{location.inDeprtCityNm}</style.AirplaneName>
														)}
														<style.AirplaneDay>{returnStart(eventDetail)}</style.AirplaneDay>
														<style.AirplaneIcon />
														{location?.inArrvCityNm && <style.AirplaneName>{location.inArrvCityNm}</style.AirplaneName>}
														<style.AirplaneDay>{returnArrival(eventDetail)}</style.AirplaneDay>
													</Flex>
												</Flex>
											</Flex>
										</style.AirplaneInfoContent>
										{/* 제주에만 나옴 */}
										{evCd.substring(0, 2) === "KJ" && (
											<style.AirplaneInfoCaution>
												해당 시간은 예정 항공 스케줄이므로 항공사 및 항공시간 변동 가능성이 있습니다. 예약확정 안내 시
												정확한 스케줄은 재안내 드립니다.
											</style.AirplaneInfoCaution>
										)}
									</style.ItineraryAirplaneInfo>
								</td>
							</tr>
							{eventDetail.accomNm && eventDetail.webDisplayCls === "B" && (
								<tr>
									<th scope="row">옵션</th>
									<td>{eventDetail.accomNm}</td>
								</tr>
							)}
							{/* 예약현황 */}
							{isReserveCount === "Y" && (
								<tr>
									<th scope="row">예약현황</th>
									<td>
										<style.StatusList>
											<StackListItem>
												예약:&nbsp;&nbsp;{eventDetail.rervCnt + eventDetail.fakeCnt - eventDetail.cancelCnt}석
											</StackListItem>
											<StackListItem>
												남은 좌석:&nbsp;&nbsp;{eventDetail.avlilBlockQty}석 (최소출발 {eventDetail.minCnt}명)
											</StackListItem>
										</style.StatusList>
									</td>
								</tr>
							)}
							{eventDetail.keypointContList.length > 0 && (
								<tr>
									<th scope="row">여행포인트</th>
									<td>
										{eventDetail.keypointContList.map((item, index) => (
											<React.Fragment key={index}>
												{item.kpointCont}
												{eventDetail.keypointContList.length !== index + 1 && <br />}
											</React.Fragment>
										))}
									</td>
								</tr>
							)}
							{eventDetail.tourPatInfo && (
								<tr>
									<th scope="row">방문도시</th>
									<td>
										<style.VisitingCityText>
											{eventDetail.tourPatInfo
												.replace(/\n/g, "")
												.split("-")
												.map((item, index) => (
													<span key={index}>{item}</span>
												))}
										</style.VisitingCityText>
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</style.ItineraryTable>
				{simpleScheYn === "Y" && simpleDays && simpleDays.length > 0 && (
					<style.ScheduleWrap>
						<style.ScheduleListWrap>
							<style.ScheduleList>
								{simpleDays.map((item, index) => {
									const ItemCount = 3;
									return (
										(index < ItemCount || showMore) && (
											<li key={index}>
												<style.ScheduleListDay>{item.dayNo}일차</style.ScheduleListDay>
												<style.ScheduleSummary>
													{item.cities.map((city, citiesIdx) => (
														<li key={citiesIdx}>
															<BlueText>{city.city}</BlueText>
															{city.tourlist && (
																<style.TouristSpot>
																	{city.tourlist.map((spot, spotIdx) => (
																		<li key={spotIdx}>{spot}</li>
																	))}
																</style.TouristSpot>
															)}
														</li>
													))}
													{item.dayNote && <style.ScheduleNotice>{item.dayNote}</style.ScheduleNotice>}
												</style.ScheduleSummary>
											</li>
										)
									);
								})}
							</style.ScheduleList>
						</style.ScheduleListWrap>
						{!usePrintView && simpleDays.length > 3 && (
							<PlusMoreButton onClick={handleScheduleButton}>간략 일정 {buttonText}</PlusMoreButton>
						)}
					</style.ScheduleWrap>
				)}
			</style.SimpleItinerary>
		</>
	);
};

const DetailNotice = (props: Pick<PageProps, "eventDetail" | "query" | "usePrintView">) => (
	<ErrorBoundary logMsg="DetailContents DetailNotice Error">
		<DetailNoticeComponent {...props} />
	</ErrorBoundary>
);

const DetailCouponComponent = (props: {evCd: string; usePrintView?: boolean}) => {
	const {evCd, usePrintView = false} = props;
	const CouponByEventFetch = useCouponByEvent(evCd);
	const coupon = CouponByEventFetch.data?.body;

	const {data: loginInfo} = useSigninUser();
	const [open] = useOverlay();
	const queryClient = useQueryClient();

	const {taggingClick} = useTaggingClick("goods_down_coupon", {evCd, selectDiv: "MiddtlButton"});

	const handleCoupon = async (coupon: CouponByEvent) => {
		const {couponNo} = coupon;
		if (loginInfo?.code !== "0000") {
			open(prop => (
				<Alert
					{...prop}
					alertType="confirm"
					text="로그인 후 이용하실 수 있습니다. <br/>로그인하시겠습니까?"
					afterConfirm={() => LoginUtil.login()}
				/>
			));
		} else {
			try {
				taggingClick(coupon);
				const body: RequestBody = {
					compId: "1",
					webCustNo: loginInfo.body.webCustNo,
					couponNo,
				};
				const result = await couponDownAsync(body);
				const {data} = result;
				if (data.errorCode === "0000") {
					open(prop => <Alert {...prop} text={data.errorMsg} />);
					queryClient.refetchQueries({queryKey: ["useCouponByEvent"], type: "active"});
				} else if (data.errorCode === "2004") {
					open(prop => <Alert {...prop} text="이미 쿠폰을 발급받으셨습니다." />);
				} else {
					alert(data.errorMsg);
				}
			} catch (error) {
				alert("오류가 발생하였습니다.");
			}
		}
	};

	return (
		<>
			{coupon && coupon.length > 0 && (
				<>
					<style.SubTitleFlexWrap>
						<SubTitle>사용 가능 쿠폰</SubTitle>
						<style.CouponTotalText>
							총 <em>{coupon.length}</em>건
						</style.CouponTotalText>
					</style.SubTitleFlexWrap>
					<style.CouponList>
						{coupon.map((item, index) => (
							<style.CouponItem key={index}>
								<style.CouponIcon>
									<i>COUPON</i>
								</style.CouponIcon>
								<style.CouponTitle>
									<style.CouponPrice>
										{item.discDiviCd === "M" ? (
											<i>{format.koKRFormatNumber(item.discAmt)}원</i>
										) : (
											<i>{item.discRate}%</i>
										)}
									</style.CouponPrice>
									<style.CouponName>{item.couponNm}</style.CouponName>
								</style.CouponTitle>
								<style.CouponButtonWrap>
									{item.validityDiviCd === "DD" ? (
										<style.CouponExpired>
											<span>발급일로부터 {item.validityDday}일 유효</span>
										</style.CouponExpired>
									) : (
										<style.CouponExpired>
											<span>
												{dayjs(item.validityFrDt).format("YYYY.MM.DD")} ~{" "}
												{dayjs(item.validityToDt).format("YYYY.MM.DD")}
											</span>
										</style.CouponExpired>
									)}
									{!usePrintView && (
										<TextButton types="basic" size="default" text="다운로드" onClick={() => handleCoupon(item)} />
									)}
								</style.CouponButtonWrap>
							</style.CouponItem>
						))}
					</style.CouponList>
					<ul>
						<BulletListItem>
							해당 쿠폰은 행사 전용 혜택으로, 회원 등급별 쿠폰을 다운받으시려면&nbsp;
							<style.TextLink>
								<a
									href={`${publicRuntimeConfig.ENV_PACKAGE_URL}/mypage/memberGrade.yb`}
									target="_blank"
									rel="noreferrer"
								>
									이곳
								</a>
							</style.TextLink>
							을 클릭해주세요.
						</BulletListItem>
					</ul>
				</>
			)}
		</>
	);
};

const DetailCoupon = (props: {evCd: string; usePrintView?: boolean}) => (
	<ErrorBoundary logMsg="DetailContents DetailCoupon Error">
		<DetailCouponComponent {...props} />
	</ErrorBoundary>
);

const DetailPriceComponent = (props: Pick<PageProps, "eventDetail">) => {
	const {eventDetail} = props;
	const eventPriceFetch = useEventPrice(eventDetail.evCd);
	const eventPrice = eventPriceFetch.data?.body;
	const price = getPrice(eventPrice);
	const TdPrice = ({type = "adult"}) => {
		const amount = {
			adult: {price: price.adult, baf: eventPrice.bafAdtCost, air: eventPrice.airTaxAdtCost},
			child: {price: price.child, baf: eventPrice.bafChdCost, air: eventPrice.airTaxChdCost},
			infant: {price: price.infant, baf: eventPrice.bafInfCost, air: eventPrice.airTaxInfCost},
		};
		const cost = amount[type];
		const isFH = isGoodsTpFH(eventDetail.goodsTp);

		return (
			<>
				{!isFH ? (
					<td>
						<style.PriceBox>
							<em>
								<strong>{price && format.koKRFormatNumber(cost.price)}</strong>원
							</em>
							{(cost.baf > 0 || cost.air > 0) && (
								<span>{`세금 및 유류할증료 ${format.koKRFormatNumber(cost.baf + cost.air)}원 포함`}</span>
							)}
						</style.PriceBox>
					</td>
				) : (
					<td>
						{cost.price > 0 ? (
							<style.PriceBox>
								<em>
									<strong>{price && format.koKRFormatNumber(cost.price)}</strong>원
								</em>
								{(cost.baf > 0 || cost.air > 0) && (
									<span>{`세금 및 유류할증료 ${format.koKRFormatNumber(cost.baf + cost.air)}원 포함`}</span>
								)}
							</style.PriceBox>
						) : (
							<style.PriceBox>별도문의</style.PriceBox>
						)}
					</td>
				)}
			</>
		);
	};

	return (
		<>
			<style.SubTitleFlexWrap>
				<SubTitle>상품가</SubTitle>
				<style.CouponTotalText>
					(예약금){" "}
					{eventPrice.depositAmt === 0 ? (
						<>별도안내</>
					) : (
						<>{`${eventPrice.depositDivi ? eventPrice.depositDiviNm : ""} ${format.koKRFormatNumber(
							eventPrice.depositAmt,
						)}원`}</>
					)}
				</style.CouponTotalText>
			</style.SubTitleFlexWrap>
			<style.ProductPriceTable>
				<TableContainer lineTop types="vertical">
					<caption>상품가 안내</caption>
					<colgroup>
						<col style={{width: "140px"}} />
						<col style={{width: "254px"}} />
						<col style={{width: "254px"}} />
						<col style={{width: "254px"}} />
					</colgroup>
					<tbody>
						<tr>
							<th>구분</th>
							<th>
								<style.AgeLimitBox>
									성인 <span>(만 12세 이상)</span>
								</style.AgeLimitBox>
							</th>
							<th>
								<style.AgeLimitBox>
									아동 <span>(만 2세 ~ 만 12세 미만)</span>
									<style.AgeLimit>{getChdAgeRange({evStartDt: eventDetail.evStartDt}).join(" ~ ")} 출생</style.AgeLimit>
								</style.AgeLimitBox>
							</th>
							<th>
								<style.AgeLimitBox>
									유아 <span>(만 2세 미만)</span>
									<style.AgeLimit>{getInfAgeRange({evStartDt: eventDetail.evStartDt}).join(" ~ ")} 출생</style.AgeLimit>
								</style.AgeLimitBox>
							</th>
						</tr>
						<tr>
							<td>총 상품가</td>
							{price && <TdPrice />}
							{price && <TdPrice type="child" />}
							{price && <TdPrice type="infant" />}
						</tr>
					</tbody>
				</TableContainer>
			</style.ProductPriceTable>
			<ul>
				<BulletListItem>
					유류할증료 및 TAX는 유가 및 환율 등에 따라 변동될 수 있으며, 아동/유아 기준은 항공사마다 상이하여 예약 후 최종
					확정됩니다.
				</BulletListItem>
			</ul>
		</>
	);
};

const DetailPrice = (props: Pick<PageProps, "eventDetail">) => (
	<ErrorBoundary logMsg="DetailContents DetailPrice Error">
		<DetailPriceComponent {...props} />
	</ErrorBoundary>
);

const DetailContentsComponent = (props: PageProps) => {
	const {eventDetail, goodsDetail, query} = props;

	return (
		<>
			<DetailNotice eventDetail={eventDetail} query={query} />
			<DetailBanner goodsCd={goodsDetail.goodsCd} goodsNm={goodsDetail.goodsNm} />
			<DetailPrice eventDetail={eventDetail} />
			<DetailCoupon evCd={eventDetail.evCd} />
		</>
	);
};

const DetailContents = (props: PageProps) => (
	<ErrorBoundary logMsg="DetailContents Error">
		<DetailContentsComponent {...props} />
	</ErrorBoundary>
);

export default DetailContents;
export {DetailBanner, DetailCoupon, DetailNotice, DetailPrice};
